// Metric help texts (Hungarian: the UI is Hungarian). One data file so the
// copy is easy to edit. Every entry answers, in this order: what it means,
// which direction is good, and a good range ONLY when a defensible general
// reference exists. No invented thresholds; where there is none we say so and
// name what to compare against. No formulas or strategy parameters.

export interface HelpEntry {
  what: string;
  direction: string;
  range: string;
}

const NO_RANGE_PREFIX = 'Nincs általános jó tartomány. ';

export const HELP: Record<string, HelpEntry> = {
  // ---- overview KPIs
  openLoan: {
    what: 'A még vissza nem fizetett kézi hitelek mai értéke USD-ben (coin-hitelnél mai áron).',
    direction: 'Az alacsonyabb kisebb kitettséget jelent; nem jó vagy rossz önmagában.',
    range: NO_RANGE_PREFIX + 'Érdemes a saját korábbi értékedhez és a Binance Marginon kívül lévő összeghez hasonlítani.',
  },
  outsideMargin: {
    what: 'A hitelből származó pénz, ami most nem a Binance Margin számlán van (Futures, Nexo, magánszámla). Ennyit kell visszahozni a visszafizetéshez.',
    direction: 'Az alacsonyabb jobb: kevesebb pénz van a Margin számlán kívül.',
    range: NO_RANGE_PREFIX + 'A nulla azt jelenti, hogy minden visszahozva; érdemes a nyitott hitel összegéhez viszonyítani.',
  },
  interest: {
    what: 'Számolt kamat: a hitelösszeg és a Binance órás kamatlábának szorzata a fennállási órákra. Számolt érték, nem tőzsdei rekord.',
    direction: 'Az alacsonyabb olcsóbb hitelezést jelent.',
    range: NO_RANGE_PREFIX + 'Az ügylet nyereségéhez érdemes hasonlítani: a kamat a költség.',
  },
  realized: {
    what: 'A lezárt kézi ügyletek eredménye USD-ben, kamattal és díjjal együtt.',
    direction: 'A magasabb jobb; a pozitív nyereség, a negatív veszteség.',
    range: 'Nulla fölött nyereséges. Más általános viszonyítás nincs; a saját korábbi ügyleteidhez hasonlíts.',
  },
  // ---- open loans table
  loanAsset: {
    what: 'A felvett coin.',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'Csak azonosít.',
  },
  openedAt: {
    what: 'A hitelfelvétel időpontja, UTC.',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'Minden időpont UTC.',
  },
  quantity: {
    what: 'A mennyiség a megjelölt eszközben.',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'Az ügylet méretét mutatja.',
  },
  entryPrice: {
    what: 'Felvételi ár: coin-hitelnél a felvett coin eladási átlagára (D24); ha még nem volt eladás (vagy nincs ügylet), a felvételi gyertya-árak mennyiséggel súlyozott átlaga (D40) — a megnevezés az ár mellett áll; stablecoin-hitelnél az ügylet referencia-coinjának ára a felvétel percében (D10).',
    direction: 'Coin-hitelnél (short) a magasabb eladási ár jobb; stablecoin-hitelnél attól függ, melyik irányra fogadtál.',
    range: NO_RANGE_PREFIX + 'A mai árhoz és az alatta lévő gyertya-árhoz érdemes hasonlítani.',
  },
  candlePrice: {
    what: 'A felvétel percének 1 perces gyertya-záróára (D9), tájékoztató árszint.',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'A tényleges eladási átlaghoz érdemes hasonlítani: eltérésük a csúszás.',
  },
  currentPrice: {
    what: 'A coin mai ára; az ár forrása a szám mellett áll (Candle, Binance spot; stablecoinnál 1:1). Hiányzó ár esetén "—" áll, soha nem nulla.',
    direction: 'Hitelnél az alacsonyabb mai ár kedvező, ha a coint eladtad.',
    range: NO_RANGE_PREFIX + 'A felvételi árhoz hasonlíts.',
  },
  deviation: {
    what: 'A mai ár eltérése a felvételi ártól, százalékban.',
    direction: 'Irány a hitel fajtájától függ: coin-hitelnél (short) a negatív eltérés kedvező, referencia-coinos stablecoin-hitelnél a pozitív.',
    range: NO_RANGE_PREFIX + 'Nulla körül nincs elmozdulás; a saját korábbi ügyleteidhez hasonlíts.',
  },
  // ---- per-loan view (D40)
  loanOutstanding: {
    what: 'Ebből a felvételből még vissza nem fizetett tőke: a felvett mennyiség mínusz a hozzá rendelt visszafizetések (D46). A hozzárendelés tételes: te választod ki, melyik visszafizetés melyik hitelt csökkenti. A kamat nincs benne.',
    direction: 'Az alacsonyabb jobb; a nulla teljesen visszafizetett.',
    range: 'Nulla = ez a felvétel vissza van fizetve.',
  },
  borrowPrice: {
    what: 'A coin ára a felvétel percében (a törési pont): a Candle 1 perces gyertyájának záróára (D9); ha a Candle nem ad árat, a Binance nyilvános (spot) ára; ha az sem, te adod meg kézzel. A forrás a szám mellett látszik. Stablecoin-hitelnél az ügylet referencia-coinjának ára (D10).',
    direction: 'Nem értékelhető irányban; a mai árhoz hasonlítva mutatja, mennyit mozdult az árfolyam.',
    range: NO_RANGE_PREFIX + 'Ha hiányzik, a sor végén lévő gombbal újra lekérdezhető.',
  },
  valueAtBorrow: {
    what: 'A felvett mennyiség értéke a felvétel árán, USD-ben (az USDT/USDC 1:1-nek számít, hipotézis).',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'A mai értékhez érdemes hasonlítani.',
  },
  valueNow: {
    what: 'A még kint lévő mennyiség értéke a mai áron, USD-ben: ennyibe kerülne most visszafizetni.',
    direction: 'Hitelnél az alacsonyabb jobb, mert kevesebbet kell kifizetni.',
    range: NO_RANGE_PREFIX + 'A kint lévő mennyiség felvételkori értékéhez hasonlíts.',
  },
  repayCostChange: {
    what: 'Mai érték mínusz a kint lévő rész felvételkori értéke, USD-ben. Pozitív: ha most fizetnéd vissza, ennyivel TÖBBBE kerül (árfolyamveszteség). Negatív: ennyivel kevesebbe kerül (nyereség). Stablecoin-hitelnél 0.',
    direction: 'Az alacsonyabb (negatívabb) jobb: olcsóbb a visszafizetés, mint amennyit a felvett coin akkor ért.',
    range: 'Nulla = nincs árfolyamkülönbség. A sor színe: figyelmeztető = drágább a visszafizetés, zöld = olcsóbb.',
  },
  repayCostPct: {
    what: 'Ugyanez a különbség százalékban, a kint lévő rész felvételkori értékéhez viszonyítva.',
    direction: 'Az alacsonyabb (negatívabb) jobb.',
    range: NO_RANGE_PREFIX + 'A nulla körüli érték csak kis árfolyammozgást jelent.',
  },
  loanStatus: {
    what: 'Nyitott: a felvételből van még vissza nem fizetett tőke. Visszafizetett (Lezárt): a hozzárendelt visszafizetések teljesen fedezik, a lezárás időpontja a „Mind” nézetben látszik.',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'Csak azonosít.',
  },
  outstanding: {
    what: 'A hitelből még vissza nem fizetett mennyiség.',
    direction: 'Az alacsonyabb jobb; a nulla teljesen visszafizetett.',
    range: 'Nulla = visszafizetve.',
  },
  deal: {
    what: 'Az ügylet, amelyhez a tétel tartozik. Az ügylet a hitel és minden belőle származó tétel (D26).',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'Kattintásra megnyílik az ügylet.',
  },
  // ---- location / breakdown / receivables
  breakdown: {
    what: 'Hol van most a hitelből származó pénz, helyszínenként, mai értéken USD-ben, az eredet-címke (ügylet) szerint (D21).',
    direction: 'Nem értékelhető irányban; a Binance Margin számlán kívüli rész a visszahozandó.',
    range: NO_RANGE_PREFIX + 'A Margin számlán kívüli részarányt érdemes a saját tervedhez viszonyítani.',
  },
  receivables: {
    what: 'Hitelből kiutalt összeg, ami még nem érkezett vissza. A célérték a kiutalt összeg (pl. 2 000 USDC), nem a mai érték.',
    direction: 'Az alacsonyabb jobb: a nulla azt jelenti, hogy minden visszaérkezett.',
    range: NO_RANGE_PREFIX + 'A fedezetlen rész a legfontosabb jelzés.',
  },
  uncovered: {
    what: 'Fedezetlen: a visszahozandó összeg azon része, amelyet a helyszínen lévő tételek mai értéke nem fedez (D37). A költés nem csökkenti a visszahozandót.',
    direction: 'Az alacsonyabb jobb; a nulla azt jelenti, hogy a mai érték fedezi.',
    range: 'Nulla = fedezett. Pozitív érték = a visszahozásig árkockázat vagy hiány van.',
  },
  recentEvents: {
    what: 'A legutóbbi naplózott események: Binance-ből megjelöltek, kézi bevitelek és számolt kamat.',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'Az események időrendben követik a pénz útját.',
  },
  eventsSource: {
    what: 'Honnan származik az esemény: kézi bevitel, a Binance-ből megjelölt rekord, vagy számolt kamat. A számolt kamat óránként könyvelődik, ezért sok sort ad; alapból látszik, de a gombbal bármikor kivehető.',
    direction: 'Nem értékelhető irányban: a szűrő csak azt dönti el, mit látsz.',
    range: NO_RANGE_PREFIX + 'Ha csak a saját bevitelt keresed, kapcsold ki a „Számolt kamat” gombot; a beállítás megmarad a böngészőben és a címsorban (könyvjelzőzhető).',
  },
  eventsPeriod: {
    what: 'Az események időpontjára szűr, UTC szerint. A „Ma” a mai UTC nap eleje óta, a „7 nap” és a „30 nap” a napok UTC-határán kezdődik. Egyéninél a „-tól” benne van, a „-ig” már nem.',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'A „Mind” az összes eseményt mutatja.',
  },
  eventQuantity: {
    what: 'Az esemény mennyisége az esemény helyszínén: a pluszjel azt jelzi, hogy a helyszínen nő a készlet (felvétel, vétel), a mínusz azt, hogy csökken (visszafizetés, eladás, kamat, transzfer).',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'Az egyenleg-rögzítés előjel nélkül látszik, az eredmény a saját előjelét viseli.',
  },
  source: {
    what: 'Az esemény forrása: Binance (megjelölt), Kézi (kézzel bevitt) vagy Számolt (kamat).',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'A számolt kamat nem tőzsdei rekord.',
  },
  price: {
    what: 'Az esemény ára. Hitelfelvételnél a gyertya-árszint (szürke), vételnél/eladásnál a tényleges ár. Hiányzó ár esetén "—".',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'A mai árhoz érdemes hasonlítani.',
  },
  // ---- mark page
  window: {
    what: 'A lekérdezett időablak, UTC. A "Ma" az aktuális UTC nap eleje óta tart.',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'Hosszú ablaknál a lekérés a Binance korlátai miatt több szeletben fut.',
  },
  account: {
    what: 'A Binance Margin számla fajtája: Cross Margin vagy egy Isolated pár.',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'Az Isolated számlánál meg kell adni a párt.',
  },
  symbols: {
    what: 'A kötés-előzmény páronként kérhető le, a kölcsön- és transzfer-előzmény coinonként.',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'Add meg azokat a párokat és coinokat, amelyekkel a kézi műveletet végezted.',
  },
  clientId: {
    what: 'A kliens-azonosító. A "gbx" kezdetű azonosítót a Margin bot adja; ezek szürkén jelennek meg, de megjelölhetők.',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'A kölcsön-előzmény nem ad kliens-azonosítót, ott csak te tudod, hogy kézi-e.',
  },
  status: {
    what: 'A tétel állapota: nincs megjelölve, kézi rögzítésre kijelölve, Margin bot, vagy már a naplóban.',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'A már naplózott tétel zárolt, csak az ügylet oldalán módosítható.',
  },
  split: {
    what: 'Egy esemény mennyisége részenként több ügylethez rendelhető (D12). Az allokációk összege nem lépheti túl az esemény mennyiségét.',
    direction: 'Nem értékelhető irányban.',
    range: 'A szerver a végleges ellenőrző: túllépésnél 409-et ad.',
  },
  // ---- deal page
  dealLoan: {
    what: 'Az ügylet még fennálló hitele mai értéken, USD-ben (a bekarikázott coin-mennyiség alatta). Amit már visszafizettél, nem szerepel benne.',
    direction: 'Az alacsonyabb jobb.',
    range: 'Nulla = az ügylet hitele teljesen visszafizetve.',
  },
  priceLevel: {
    what: 'Stablecoin-hitelnél az ügylet referencia-coinjának 1 perces záróára a felvétel percében (D9, D10); coin-hitelnél a felvett coin eladási átlaga (D24).',
    direction: 'Attól függ, melyik irányra fogadtál.',
    range: NO_RANGE_PREFIX + 'A mai árhoz hasonlíts.',
  },
  outsideDeal: {
    what: 'A hitelből származó, de a Binance Marginon kívül lévő, visszahozandó összeg.',
    direction: 'Az alacsonyabb jobb.',
    range: 'Nulla = minden visszahozva.',
  },
  dealInterest: {
    what: 'Számolt kamat: hitelösszeg × a Binance órás kamatlába × a fennállási órák (D17). Nem tőzsdei rekord.',
    direction: 'Az alacsonyabb jobb.',
    range: NO_RANGE_PREFIX + 'Az ügylet nyereségéhez viszonyítsd.',
  },
  unrealized: {
    what: 'Amit az ügylet ma hátrahagyna, ha minden azonnal rendeződne: az ügylet egyenlegei mai áron, mínusz a fennálló hitel, a saját tőke, a számolt kamat és a díjak. A Futures-alap csak a bevitt egyenleg (D23).',
    direction: 'A magasabb jobb; pozitív a nyereség.',
    range: 'Nulla fölött nyereséges; más általános viszonyítás nincs.',
  },
  flow: {
    what: 'A pénz útja: hitel, konverziók, helyszínek, visszafizetés. A dobozok az ügylet eseményeiből épülnek.',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'A narancs keretes helyszín visszahozandó összeget tart.',
  },
  avgCost: {
    what: 'Átlagár: az ügyletbe allokált vételek súlyozott átlagára coinonként (D11).',
    direction: 'Vételnél az alacsonyabb átlagár jobb.',
    range: NO_RANGE_PREFIX + 'A mai árhoz hasonlíts.',
  },
  allocation: {
    what: 'Az esemény mennyiségének hányada, amelyet ehhez az ügylethez rendeltek (D12).',
    direction: 'Nem értékelhető irányban.',
    range: 'A 100% az egész eseményt jelenti.',
  },
  // ---- deals list
  loanSummary: {
    what: 'Az ügylet hiteleinek összefoglalója.',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'Csak tájékoztat.',
  },
  result: {
    what: 'Az ügylet eredménye USD-ben. Lezárt ügyletnél realizált, nyitottnál nem szerepel.',
    direction: 'A magasabb jobb.',
    range: 'Nulla fölött nyereséges.',
  },
  // ---- locations
  valueUsd: {
    what: 'Mai érték USD-ben; az árakat a Candle szolgáltatás adja (a USD ≈ USDT/USDC 1:1 feltevéssel).',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'Csak tájékoztat.',
  },
  costValue: {
    what: 'Bekerülési érték USD-ben: az ügyletbe allokált vételek átlagára (D11) szorozva a mennyiséggel; a stablecoin névértéken számít. Ha nincs vétel vagy az ellenérték nem USD-stablecoin, „—” látszik.',
    direction: 'Nem értékelhető irányban; a mai értékhez viszonyítva mutatja a nem realizált különbséget.',
    range: NO_RANGE_PREFIX + 'A mai értékhez hasonlíts.',
  },
  unrealizedPos: {
    what: 'Nem realizált eredmény a pozíción: mai érték mínusz bekerülési érték, USD-ben. Csak akkor látszik, ha mindkettő ismert.',
    direction: 'A magasabb jobb; a negatív érték árfolyamveszteséget jelent a pozíción.',
    range: NO_RANGE_PREFIX + 'Csak tájékoztat, amíg a pozíció nincs eladva.',
  },
  returnableCol: {
    what: 'Visszahozandó: amit az ügyletnek még vissza kell kapnia erről a helyszínről a Binance Marginra (D37). Egy követelés csak egy soron látszik, hogy az összeg ne számítson kétszer.',
    direction: 'Az alacsonyabb jobb: nulla, ha minden visszakerült.',
    range: NO_RANGE_PREFIX + 'A fedezetlen részt az Áttekintés mutatja.',
  },
  lastMovement: {
    what: 'Az utolsó, nem visszavont esemény ideje, amely ezt a helyszín × coin × eredet tételt mozgatta (UTC).',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'Régóta nem mozgó tétel esetén érdemes ránézni, hogy még kell-e.',
  },
  origin: {
    what: 'Az eredet-címke: az ügylet, amelynek hitelébe vezethető vissza a tétel (D21).',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'Az eredet átöröklődik minden konverzióra és transzferre.',
  },
  // ---- entry
  entryPriceField: {
    what: 'A vétel/eladás ára. Üresen hagyva a szerver az esemény percének 1 perces gyertya-záróárát használja (D9).',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'A mellette látszó gyertya-árhoz érdemes hasonlítani.',
  },
  preview: {
    what: 'Kettős könyvelés előnézet: minden tétel két helyszín egyenlegét mozgatja (D21). Az előnézet tájékoztató, a végleges könyvelést a szerver végzi.',
    direction: 'Nem értékelhető irányban.',
    range: 'A tartozik és követel oldal összege mindig egyenlő.',
  },
  // ---- D46-D49: repay links, unassigned repays, interest, price sources
  realizedResult: {
    what: 'Realizált eredmény USD-ben: (a felvétel árszintje − a visszafizetés percének árszintje) × a hozzárendelt mennyiség. Pozitív (zöld): olcsóbban fizetted vissza, mint ahogy felvetted (nyereség); negatív (figyelmeztető szín): drágábban. A „kamattal” érték ebből levonja a fizetett kamat USD-értékét.',
    direction: 'A magasabb (pozitívabb) jobb.',
    range: 'Nulla = a visszafizetés ára megegyezik a felvételivel. Ismeretlen ár esetén „—” és az ok áll, soha nem nulla.',
  },
  loanClosed: {
    what: 'Lezárt: a hitelből nincs több kint lévő tőke. A lezárás időpontja az utolsó hozzárendelt visszafizetés ideje. A lezárt hitel változatlanul megmarad a felvételi árával (törési pont).',
    direction: 'Nem értékelhető irányban.',
    range: 'Nincs általános jó tartomány.',
  },
  interestPaid: {
    what: 'A hitelhez rendelt visszafizetésekben fizetett kamat: a visszafizetés kamat-része a hozzárendelt tőkével arányosan oszlik a hitelek közt (D48). USD-ben a visszafizetés percének árán értékelt.',
    direction: 'Az alacsonyabb jobb.',
    range: 'Nincs általános jó tartomány.',
  },
  unassignedRepay: {
    what: 'Hozzárendelésre vár (D47): a visszafizetésnek az a tőke-része, amelyet még egyik hitelhez sem kötöttél. A helyszín tartozását csökkenti, de egyik hitelt sem; a rendszer nem talál ki párosítást. A kamat-rész sosem vár hozzárendelésre.',
    direction: 'A nulla a cél: minden visszafizetés legyen hitelhez rendelve.',
    range: 'Nulla = nincs váró visszafizetés. A Hitelek oldalon a „Hozzárendelés” gombbal pótolható.',
  },
  repayLinks: {
    what: 'Melyik hitelfelvételt fizeti vissza a visszafizetés (D46). A javaslat „legkedvezőbb először”: a legmagasabb felvételi árú hitelt zárja előbb, mert azt a legolcsóbb ma visszafizetni. Átírhatod: a pipa és a mennyiség szabad; a szerver mentéskor ellenőrzi. Ha a visszafizetés több, mint a hitel, a többlet kamat, és két úton rendezheted. (1) Az „Ebből kamat” mezőbe beírod a kamatot: a tőke-rész (mennyiség − kamat) rendelhető hitelhez, részleges visszafizetésnél a hitel maradéka nyitva marad. (2) A „Teljes lezárás” jelölés a kijelölt hitelek teljes kint lévő részét tőkének veszi, a többletet kamatnak: a kamat itt csak olvasható. Mentéskor a kamat-rész a MEGLÉVŐ visszafizetésen változik, nem jön létre új bejegyzés; a módosítás naplózódik.',
    direction: 'Nem értékelhető irányban; a realizált eredmény mutatja a különbséget.',
    range: 'A hozzárendelt összeg legfeljebb a tőke-rész, hitelenként legfeljebb a kint lévő mennyiség. Teljes lezárásnál minden kijelölt hitel a teljes kint lévő részével szerepel.',
  },
  interestPart: {
    what: 'A visszafizetett mennyiségből a kamat (D48). Mindig megadhatod kézzel. A tőke-rész = mennyiség − kamat; csak a tőke köthető hitelhez. Egyéni helyszínen a kamat kamatköltségként könyvelődik; Binance-helyszínen a már számolt kamatot rendezi, teljes lezáráskor a szerver a különbséget egyszer kiigazítja (true-up).',
    direction: 'Az alacsonyabb jobb.',
    range: 'Nulla és a visszafizetett mennyiség között; üresen 0.',
  },
  closeMode: {
    what: 'Teljes lezárás: a tőke a kijelölt hitelek teljes kint lévő része, a beírt mennyiség többlete a kamat (előtöltve, átírható). A tőkének pontosan egyeznie kell a kijelölt hitelek kint lévő részével. A true-up előnézete várható érték; a végleges kiigazítást a szerver számolja, a hitel-számla pontosan nullára zár.',
    direction: 'Nem értékelhető irányban.',
    range: 'Nincs általános jó tartomány.',
  },
  manualPrice: {
    what: 'Az árszint forrásai sorrendben: Candle 1 perces gyertya, majd a Binance nyilvános (spot) ára, majd te kézzel. Ha az „Ár kézzel” mezőt kitöltöd, az nyer, és „kézi” forrással kerül mentésre; a kézi árat az újraszámítás csak megerősítéssel írja felül.',
    direction: 'Nem értékelhető irányban.',
    range: 'Pozitív decimális szám; üresen a szerver számolja.',
  },
  priceSource: {
    what: 'Az ár forrása: Candle (1 perces gyertya), Binance spot (a Candle nem adott árat, a Binance nyilvános ára került be), kézi (te adtad meg), 1:1 (stablecoin, nincs lekérdezés).',
    direction: 'Nem értékelhető irányban.',
    range: 'A Candle az elsődleges; a kézi ár a legerősebb, mert te döntöttél róla.',
  },
  // ---- D51: pre-history marks, D52: closed loans report
  prehistoryCredit: {
    what: 'A nyitott hitel összege a rögzített felvételekből indul ki (D53). Az előzményként jelölt (D51) visszafizetés a MoneyFlow indulása előtti, nem rögzített hitelt törlesztett, ezért nem csökkenti ezt az összeget. A megjegyzés megmutatja, mennyit hagyott ki a levonásból; a könyvelés szerinti hitelszámla-egyenleg pontosan ennyivel kevesebb.',
    direction: 'Nem értékelhető irányban: csak azt mutatja, hogy a nyitott hitel és a hitelszámla-egyenleg miért tér el.',
    range: NO_RANGE_PREFIX + 'Ha a jelölést visszavonod, vagy a visszafizetést hozzárendeled egy rögzített hitelhez, a megjegyzés eltűnik.',
  },
  prehistoryPart: {
    what: 'Előzmény-rész: a visszafizetésnek az a tőke-része, amelyet előzményként jelöltél, vagyis a MoneyFlow indulása előtti, nem rögzített hitelt fizetett vissza. Ez a rész nem vár hozzárendelésre, és nem csökkent egyetlen rögzített hitelt sem.',
    direction: 'Nem értékelhető irányban: a jelölés csak azt rögzíti, hogy a visszafizetésnek nincs párja a rendszerben.',
    range: NO_RANGE_PREFIX + 'A jelölés visszavonható; ha a visszafizetést később teljesen hozzárendeled, a jelölés magától megszűnik.',
  },
  prehistoryReport: {
    what: 'Az időszakban előzményként jelölt visszafizetések, helyszín és coin szerint: olyan régi hitelek törlesztései, amelyek a MoneyFlow-ban nincsenek rögzítve, ezért nem szerepelnek a lezárt hitelek sorai között.',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'Az összeg coinonként értendő, különböző coinok mennyisége nem adható össze.',
  },
  closedPeriod: {
    what: 'A lezárás időpontjára szűr, UTC naptári napok szerint, mindkét határ benne van. A lezárás időpontja a hitelhez rendelt utolsó visszafizetés ideje. Az „Ez a hónap” és az „Idei év” a teljes UTC hónapot, illetve évet jelenti.',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'A „Mind” minden lezárt hitelt mutat. A szűrés a címsorban is megjelenik, ezért könyvjelzőzhető.',
  },
  closedAt: {
    what: 'Mikor záródott le a hitel: a hozzárendelt visszafizetések közül az utolsó ideje, UTC.',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'Az időszak-szűrő erre az időpontra vonatkozik.',
  },
  loanDuration: {
    what: 'A felvétel és a lezárás között eltelt idő napokban, egy tizedessel.',
    direction: 'A rövidebb általában olcsóbb, mert kevesebb kamat gyűlik; de a hitel célja dönt.',
    range: NO_RANGE_PREFIX + 'A fizetett kamathoz és a saját korábbi hiteleidhez érdemes hasonlítani.',
  },
  closedRepayQuantity: {
    what: 'Ennyi tőkét fizettek vissza ebből a visszafizetésből erre a hitelre. Egy visszafizetés több hitelt is fedezhet, ezért lehet kevesebb, mint a visszafizetés teljes összege.',
    direction: 'Nem értékelhető irányban.',
    range: NO_RANGE_PREFIX + 'Az összegük a hitel visszafizetett tőkéje.',
  },
  closedRepayInterest: {
    what: 'A visszafizetés kamat-részéből erre a hitelre jutó mennyiség, a hozzárendelt tőkével arányosan (D48).',
    direction: 'Az alacsonyabb jobb.',
    range: NO_RANGE_PREFIX + 'A hitel teljes fizetett kamatának része.',
  },
  closedRepayPrice: {
    what: 'A coin ára a visszafizetés percében; ebből és a felvételi árból számolódik az eredmény. Ismeretlen ár esetén „—” és az ok áll.',
    direction: 'Hitelnél az alacsonyabb visszafizetési ár kedvező.',
    range: NO_RANGE_PREFIX + 'A felvételi árhoz hasonlíts.',
  },
  closedPrincipal: {
    what: 'A lezárt hitelekre visszafizetett tőke összesen, coinonként. A kamat nincs benne.',
    direction: 'Nem értékelhető irányban; a felvett mennyiséggel egyezik, mert csak teljesen visszafizetett hitelek szerepelnek itt.',
    range: NO_RANGE_PREFIX + 'Csak tájékoztat.',
  },
  snapshot: {
    what: 'Egyenleg-rögzítés (D36): megadod a helyszín és coin jelenlegi egyenlegét; a különbség a Költésre (csökkenés) vagy a Megmagyarázatlan beérkezésre (növekedés) könyvelődik.',
    direction: 'Nem értékelhető irányban.',
    range: 'Nulla különbség = a napló és a valódi egyenleg egyezik.',
  },
  resultType: {
    what: 'Eredmény (D38): előjeles összeg a Kereskedési eredmény számlával szemben; a veszteség negatív.',
    direction: 'A magasabb (pozitívabb) jobb.',
    range: 'Nulla fölött nyereség.',
  },
  returnableFlag: {
    what: 'Ha be van pipálva, a kiutalt összegnek vissza kell érkeznie; a Visszahozandó és a fedezetlen részben megjelenik (D37).',
    direction: 'Nem értékelhető irányban.',
    range: 'Költésnél ne pipáld be: a költés nem csökkenti a visszahozandót.',
  },
};

export function helpFor(key: string): HelpEntry | undefined {
  return HELP[key];
}
